import { useMemo } from 'react';

import styled from '@emotion/styled';
import {
  Badge,
  CopyButton,
  HoverPopover,
  Typography,
  useMouseFollowPopover,
} from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { EllipsisText, PageHeader, pageHeaderClasses } from '@kc-console/shared-ui';

import PanelActions from './PanelActions';

import type { PropsWithChildren } from 'react';
import type { BadgeColor } from '@kc-console/common-ui';
import type { DefaultItemsType, PanelHeaderProps } from './types';

const DEFAULT_SELECTED_ITEM = { id: '', name: '' };
const MULTI_SELECT_POPOVER_MIN_COUNT = 3;
const LABEL_SEPARATOR = ', ';
export const POPOVER_DISPLAY_LIMIT = 20;

// Popover 테마가 Paper를 transparent/relative/no-shadow로 초기화하고,
// 커스텀 Popover 래퍼가 anchorOrigin 존재 여부로 코드 경로를 분기하므로 sx에서 명시 복원이 필요.
const POPOVER_PAPER_SX = {
  position: 'absolute',
  maxWidth: 576,
  p: '8px',
  overflowWrap: 'break-word',
  backgroundColor: 'white',
  borderRadius: '2px',
  boxShadow: '0px 4px 8px 0px rgba(19, 22, 25, 0.15), 0px 0px 1px 0px rgba(19, 22, 25, 0.15)',
} as const;

export const computeMultiSelectPopoverView = (
  labels: string[] | undefined,
  overflowText: (remainCount: number) => string,
): { copyValue: string; popoverDisplayLabel: string } => {
  if (!labels?.length) return { copyValue: '', popoverDisplayLabel: '' };
  const copyValue = labels.join('\n');
  if (labels.length <= POPOVER_DISPLAY_LIMIT) {
    return { copyValue, popoverDisplayLabel: labels.join(LABEL_SEPARATOR) };
  }
  const displayed = labels.slice(0, POPOVER_DISPLAY_LIMIT).join(LABEL_SEPARATOR);
  const remainCount = labels.length - POPOVER_DISPLAY_LIMIT;
  return {
    copyValue,
    popoverDisplayLabel: `${displayed} ${overflowText(remainCount)}`,
  };
};

/**
 * [ 패널 레이아웃 하위 패널색션 컴포넌트의 headerView prop 에서 사용하는 패널 해더 컴포넌트 ]
 *
 * - 선택된 리소스의 갯수에 따라 패널 상단 헤더에 보여줄 컨텐츠를 렌더링해주는 용도로 사용합니다.
 * - shared-ui/PageHeader 컴포넌트를 랩핑하여 제공합니다.
 * - PageHeader 와 다르게 타이틀이 길어질 경우, 말줄임과 함께 툴팁 메세지가 표시됩니다.
 *
 * @param props
 * @param props.getSingleTitle - 하나의 리소스 선택 시 보여지는 헤더 타이틀을 구성하는 함수
 * @param props.getSingleLinkPath - 하나의 리소스 선택 시 우측 링크아이콘의 href 를 구성하는 함수
 * @param props.selectedItems - 여러개 선택시 헤더 타이틀 표시를 위한 선택된 리소스들의 배열
 * @param props.selectedItemKey - selectedItems의 각 항목에서 사용할 고유 식별자 키 (optional 하지 않은 값의 키)
 * @param props.getMultiSelectLabels - 다중 선택 시 popover에 표시할 항목별 라벨 배열을 반환하는 함수
 *
 * @example
 * ```tsx
 * <PanelHeader
 *   getSingleTitle={(selectedItem) => getDetailTitleWithIdName({ id: selectedItem.id, name: selectedItem.name })}
 *   getSingleLinkPath={getRouterPath(ROUTE_PATH.NETWORK_DETAIL, { vpcId: selectedItem.id })}
 *   selectedItems={selectedItems}
 *   selectedItemKey={'id'}
 * />
 * ```
 */
export default function PanelHeader<T extends DefaultItemsType = DefaultItemsType>(
  props: PanelHeaderProps<T>,
) {
  const {
    getSingleTitle,
    getSingleLinkPath,
    selectedItems,
    selectedItemKey,
    getMultiSelectLabels,
    multiSelectPopoverMinCount = MULTI_SELECT_POPOVER_MIN_COUNT,
    onClose,
    hideCloseButton,
    badgeLabel,
    badgeColor = 'blue',
    ...others
  } = props;

  const isSingleTitle = selectedItems.length === 1;
  const selectedItem = selectedItems[0] ?? { ...DEFAULT_SELECTED_ITEM, [selectedItemKey]: '' };
  const singleTitle = isSingleTitle ? getSingleTitle(selectedItem) : undefined;
  const singleLinkPath = isSingleTitle ? getSingleLinkPath?.(selectedItem) : undefined;

  const shouldShowMultiSelectPopover =
    !isSingleTitle && !!getMultiSelectLabels && selectedItems.length >= multiSelectPopoverMinCount;

  const multiSelectLabels = shouldShowMultiSelectPopover
    ? getMultiSelectLabels(selectedItems)
    : undefined;

  const titleComponent = useMemo(
    () =>
      isSingleTitle ? (
        <SingleSelectedTitle badgeLabel={badgeLabel} badgeColor={badgeColor}>
          {singleTitle}
        </SingleSelectedTitle>
      ) : (
        <MultiSelectedTitle
          selectedCount={selectedItems.length}
          multiSelectLabels={multiSelectLabels}
        />
      ),
    [isSingleTitle, singleTitle, selectedItems.length, badgeLabel, badgeColor, multiSelectLabels],
  );

  return (
    <StyledPageHeader
      title={titleComponent}
      mainActions={
        <PanelActions
          singleLinkPath={singleLinkPath}
          onClose={onClose}
          hideCloseButton={hideCloseButton}
          disabledLinkButton={selectedItems.length > 1}
        />
      }
      {...others}
    />
  );
}

interface SingleSelectedTitleProps extends PropsWithChildren {
  badgeColor?: BadgeColor;
  badgeLabel?: string;
}

const SingleSelectedTitle = ({ children, badgeLabel, badgeColor }: SingleSelectedTitleProps) => {
  return (
    <TitleWithBadge>
      {badgeLabel && <StyledBadge label={badgeLabel} color={badgeColor} variant="outline" />}
      <EllipsisText variant="heading/semibold/3xl">{children}</EllipsisText>
    </TitleWithBadge>
  );
};

interface MultipleItemsTitleProps {
  selectedCount: number;
  multiSelectLabels?: string[];
}

const MultiSelectedTitle = ({ selectedCount, multiSelectLabels }: MultipleItemsTitleProps) => {
  const { t } = useTranslation();
  const { onMouseEnter, onMouseMove, onMouseLeave, paperRef, ...popoverProps } =
    useMouseFollowPopover({ openTimeout: 150, closeTimeout: 0 });
  const hasLabels = !!multiSelectLabels?.length;

  const { copyValue, popoverDisplayLabel } = useMemo(
    () =>
      computeMultiSelectPopoverView(multiSelectLabels, remainCount =>
        t('layout:panel.header.selected_overflow', { count: remainCount }),
      ),
    [multiSelectLabels, t],
  );

  const hoverHandlers = hasLabels ? { onMouseEnter, onMouseMove, onMouseLeave } : {};

  return (
    <MultiSelectedTitleWrapper>
      <Typography
        variant="heading/bold/2xl"
        aria-describedby={hasLabels ? popoverProps.id : undefined}
        {...hoverHandlers}
      >
        {t('layout:panel.header.selected_count', { count: selectedCount })}
      </Typography>
      {hasLabels && (
        <>
          <CopyButton
            value={copyValue}
            messageBeforeCopy={t('layout:panel.header.copy_all_selected')}
            onClick={e => e.stopPropagation()}
          />
          <HoverPopover
            {...popoverProps}
            // 마우스 아래 고정 표시 (calcSmartPosition의 flip 방지). marginThreshold는 MUI 재배치 여유값.
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            marginThreshold={10}
            slotProps={{ paper: { ref: paperRef, sx: POPOVER_PAPER_SX } }}
          >
            <Typography variant="label/regular/xs">{popoverDisplayLabel}</Typography>
          </HoverPopover>
        </>
      )}
    </MultiSelectedTitleWrapper>
  );
};

const MultiSelectedTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleWithBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
`;

const StyledBadge = styled(Badge)`
  flex-shrink: 0;
  white-space: nowrap;
`;

const StyledPageHeader = styled(PageHeader)`
  width: 100%;
  min-width: 0;
  overflow: hidden;

  .${pageHeaderClasses.mainContainer} {
    min-width: 0;
  }

  .${pageHeaderClasses.essentials} {
    overflow: hidden;
    min-width: 0;
  }

  .${pageHeaderClasses.textContainer} {
    min-width: 0;
  }

  .${pageHeaderClasses.title} {
    min-width: 0;
  }

  .${pageHeaderClasses.mainActions} {
    flex-shrink: 0;
  }
`;
